<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { LANG_ENGLISH, LANG_FRENCH, CURRENCY_USD } from '@/constants';
import { useCurrencyStore } from '@/store/CurrencyStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useToast } from 'primevue/usetoast';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const { t } = useI18n({ useScope: 'global' });

const currencyStore = useCurrencyStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const toast = useToast();
const listCurrency = computed(() => {
  return currencyStore.selectListCurrency;
});

const currencyLabel = ref({
  english: t('promos.labels.english'),
  usd: '(' + CURRENCY_USD + ')',
  france: t('promos.labels.french')
});

const isLoading = computed(() => {
  return currencyStore.selectIsLoading;
});
const isUpdate = computed(() => currencyStore.selectIsUpdate);

onMounted(() => {
  currencyStore.getListCurrency();
});

const initCurrencyEn = ref({
  value: '',
  isEdit: false
});

const initCurrencyFr = ref({
  value: '',
  isEdit: false
});

const onEdit = (languageNumber: number) => {
  const defaultCurrency = listCurrency.value?.currencies?.length ? listCurrency.value.currencies[languageNumber].displayCur : '';
  if (languageNumber === 1) {
    initCurrencyEn.value = { isEdit: true, value: defaultCurrency };
  } else {
    initCurrencyFr.value = { isEdit: true, value: defaultCurrency };
  }
};

const onSave = async () => {
  const payload = {
    currencies: [
      {
        displayCur: initCurrencyFr.value.isEdit && initCurrencyFr.value.value ? initCurrencyFr.value.value : listCurrency.value?.currencies[0]?.displayCur,
        language: LANG_FRENCH,
        cur: CURRENCY_USD
      },
      {
        displayCur: initCurrencyEn.value.isEdit && initCurrencyEn.value.value ? initCurrencyEn.value.value : listCurrency.value?.currencies[1]?.displayCur,
        language: LANG_ENGLISH,
        cur: CURRENCY_USD
      }
    ]
  };
  await currencyStore.updateCurrency(payload);
  initCurrencyEn.value.isEdit = false;
  initCurrencyFr.value.isEdit = false;
  if (isUpdate.value) {
    toast.add({ severity: 'success', summary: '', detail: t('notification.update_success'), life: 3000 });
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('notification.update_error'), life: 5000 });
  }
};

const displayCurrency = (currencyLength: number, langNumber: number) => {
  return currencyLength ? `(${listCurrency.value.currencies?.[langNumber].cur})` : currencyLabel.value.usd;
};
</script>
<template>
  <div class="currency-admin">
    <LoadingSpinner v-if="isLoading" />
    <div class="card">
      <div class="wrapper">
        <div class="font-bold text-lg ml-2 mb-4">{{ $t('management.currency.your_language') }}</div>

        <div class="currency-style mb-2">
          <div class="currency-title">
            <img src="/canada-flag.jpg" alt="Logo" class="w-3rem" />
            <div class="ml-2">
              {{
                listCurrency?.currencies?.[1].language == LANG_ENGLISH
                  ? `${currencyLabel.english}
            ${displayCurrency(listCurrency?.currencies?.length, 1)} `
                  : ''
              }}
            </div>
          </div>
          <div class="w-20 text-center">{{ $t('management.currency.currency') }} {{ displayCurrency(listCurrency?.currencies?.length, 1) }}</div>
          <div v-if="!initCurrencyEn.isEdit" class="currency-action">
            <div>{{ $t('management.currency.display_currency') }} ({{ listCurrency?.currencies?.length ? listCurrency.currencies?.[1]?.displayCur : '' }})</div>
            <div class="w-20 text-right">
              <Button class="edit-btn" v-if="authorizations?.configurationPage?.currency_edit" @click="onEdit(1)" :label="$t('common.buttons.edit')"></Button>
            </div>
          </div>
          <div v-else class="currency-action">
            <div class="currency-inputs">
              {{ $t('management.currency.display_currency') }}
              <InputText v-model="initCurrencyEn.value" class="currency-input" />
            </div>
            <div class="action-btn">
              <Button @click="onSave" class="save-btn mr-2 text-right" :label="$t('common.buttons.save')"> </Button>
              <Button @click="initCurrencyEn = { isEdit: false, value: listCurrency?.currencies[1]?.displayCur }" class="cancel-btn" :label="$t('common.buttons.cancel')"></Button>
            </div>
          </div>
        </div>
        <div class="currency-style">
          <div class="currency-title">
            <img src="/canada-flag.jpg" alt="Logo" class="w-3rem" />
            <div class="ml-2">
              {{
                listCurrency?.currencies?.[0].language == 'fr'
                  ? `${currencyLabel.france}
            ${displayCurrency(listCurrency?.currencies?.length, 0)} `
                  : ''
              }}
            </div>
          </div>
          <div class="w-20 text-center">{{ $t('management.currency.currency') }} {{ displayCurrency(listCurrency?.currencies?.length, 0) }}</div>
          <div v-if="!initCurrencyFr.isEdit" class="currency-action">
            <div>{{ $t('management.currency.display_currency') }} ({{ listCurrency?.currencies?.length ? listCurrency.currencies?.[0]?.displayCur : '' }})</div>
            <div class="w-20 text-right">
              <Button class="edit-btn" v-if="authorizations?.configurationPage?.currency_edit" @click="onEdit(0)" :label="$t('common.buttons.edit')"></Button>
            </div>
          </div>
          <div v-else class="currency-action">
            <div class="currency-inputs">
              {{ $t('management.currency.display_currency') }}
              <InputText v-model="initCurrencyFr.value" />
            </div>
            <div class="action-btn">
              <Button @click="onSave" class="save-btn mr-2 text-right" :label="$t('common.buttons.save')"> </Button>
              <Button @click="initCurrencyFr = { isEdit: false, value: listCurrency?.currencies[0]?.displayCur }" class="cancel-btn" :label="$t('common.buttons.cancel')"></Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.wrapper {
  .w-10 {
    width: 15% !important;
  }

  .w-20 {
    width: 20%;
  }

  .w-50 {
    width: 50%;
  }

  p {
    font-size: 20px;
  }

  .currency-eng {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .currency-fr {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.currency-admin {
  .currency-style {
    display: flex;
    align-items: center;
    justify-content: space-between;
    .currency-title {
      display: flex;
      align-items: center;
    }
    .currency-action {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 50%;
    }
  }
  .p-inputtext {
    width: 50%;
  }
}
</style>
